/**
 * Structural validator for `CanonicalSkillV3Input`.
 *
 * Scope is deliberately narrow: it decides whether a composite *structure* is
 * well-formed. It does not price anything, does not consult a technical
 * profile, and does not decide whether a well-formed structure can actually be
 * executed — capability gating lives at the loader/compiler boundary, and
 * budget/burst live with their existing owners.
 *
 * Fail-closed: an unknown discriminant is an error, never a coerced default.
 * No value is repaired, rounded, clamped or reordered on the way through — a
 * player's authored count of 5 either survives as 5 or is reported, and the
 * stage list keeps its authored order because that order is itself authority.
 */
import { castReachLimits } from '../burst/profile.js';
import { FORMATION_FIELD_RULES } from './field-rules.js';
import { dotRefreshKeyIssues, type CanonicalValidationIssue } from './contract.js';
import { SVP_0_1 } from '../profile.js';
import { elementPackage } from '../sim/element-package.js';
import { checkStatusRules, ELEMENT_STATUS_NAMES, elementStatusMismatch, type StatusDef, type StatusStage } from './skill-status-rules.js';
import { DASH_ATTACK_RANGE_MAX_M, DASH_RADIUS_MIN_M, dashChainJumpIsReachable, dashRadiusIsValid, dashRangeIsValid, dashScheduleRefusals, type DashStageFacts } from './dash-attack.js';
import { chainCarriersSurvive, chainCountIsValid, chainFilterTimingIsLegal, chainSpanTicks, chainStatusDurationTicks, type ChainCarrierLife, chainRangeIsValid, deliveryMaxLifeTicks, CHAIN_RANGE_MAX_M, CHAIN_RANGE_MIN_M } from './chain-formation.js';
import { expandSpatialPattern } from './spatial-pattern.js';
import type { SkillApplicationV2 } from './types.js';
import {
  CANONICAL_SKILL_V3_SCHEMA_VERSION,
  CHAIN_PICKS_V1,
  MEMBER_SCHEDULE_KINDS_V1,
  SPATIAL_ATTACK_PATTERN_V1_SCHEMA_VERSION,
  SPATIAL_FORMATION_KINDS_V1,
  STAGE_EVENT_ANCHORS_V1,
  STAGE_EVENT_KINDS_V1,
  STAGE_EVENT_PER_V1,
  STAGE_EVENT_SOURCES_V1,
  STAGE_TIMING_KINDS_V1,
  STATUS_HOOK_EVENTS_V1,
  SWEEP_STRENGTHS_V1,
  VOLLEY_SCHEDULE_KINDS_V1,
  type AttackStageV1,
  type CanonicalSkillV3,
  type CanonicalSkillV3Input,
  type SpatialAttackPatternV1,
  type StageTimingV1,
} from './composite-types.js';

export type CompositeValidationResult =
  | { readonly ok: true; readonly skill: CanonicalSkillV3 }
  | { readonly ok: false; readonly issues: readonly CanonicalValidationIssue[] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A count of real things: strictly positive, integral, finite. */
function isPositiveInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

/** A tick offset: integral, finite, and allowed to be zero. */
function isNonNegativeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/** A measured quantity: finite, so NaN/Infinity never reach a solver. */
function isFinitePositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

class IssueBag {
  readonly issues: CanonicalValidationIssue[] = [];
  add(path: string, code: string, message: string): void {
    this.issues.push({ path, code, message });
  }
}

function validateFormation(formation: unknown, path: string, bag: IssueBag): void {
  if (!isRecord(formation)) {
    bag.add(path, 'COMPOSITE_FORMATION_INVALID', 'formation must be an object');
    return;
  }
  const kind = formation.kind;
  if (typeof kind !== 'string' || !(SPATIAL_FORMATION_KINDS_V1 as readonly string[]).includes(kind)) {
    bag.add(`${path}.kind`, 'COMPOSITE_FORMATION_KIND_UNKNOWN', `unknown formation kind ${String(kind)}`);
    return;
  }

  // `SINGLE` is the one formation whose count is fixed by its own meaning.
  if (kind === 'SINGLE') {
    if (formation.count !== 1) {
      bag.add(`${path}.count`, 'COMPOSITE_FORMATION_COUNT_INVALID', 'SINGLE formation must declare count 1');
    }
  } else if (!FORMATION_FIELD_RULES.memberCount.holds(formation.count)) {
    // A multi-member formation with fewer than two members is a mis-authored
    // SINGLE, not a formation. Rejecting is what keeps an authored count
    // honest — the alternative would be quietly treating count 0 as 1.
    bag.add(`${path}.count`, 'COMPOSITE_FORMATION_COUNT_INVALID', `${kind} formation requires an integer count of at least 2`);
  }

  switch (kind) {
    case 'FAN':
      if (!FORMATION_FIELD_RULES.angleDeg.holds(formation.spreadAngleDeg)) {
        bag.add(`${path}.spreadAngleDeg`, FORMATION_FIELD_RULES.angleDeg.code, 'spreadAngleDeg must be a finite number');
      }
      break;
    case 'PARALLEL':
      if (!FORMATION_FIELD_RULES.spacingM.holds(formation.spacingM)) {
        bag.add(`${path}.spacingM`, FORMATION_FIELD_RULES.spacingM.code, 'spacingM must be a finite positive number');
      }
      break;
    case 'RADIAL':
      if (!FORMATION_FIELD_RULES.angleDeg.holds(formation.initialAngleDeg)) {
        bag.add(`${path}.initialAngleDeg`, FORMATION_FIELD_RULES.angleDeg.code, 'initialAngleDeg must be a finite number');
      }
      break;
    case 'RING':
      if (!FORMATION_FIELD_RULES.angleDeg.holds(formation.initialAngleDeg)) {
        bag.add(`${path}.initialAngleDeg`, FORMATION_FIELD_RULES.angleDeg.code, 'initialAngleDeg must be a finite number');
      }
      if (!FORMATION_FIELD_RULES.radiusM.holds(formation.radiusM)) {
        bag.add(`${path}.radiusM`, FORMATION_FIELD_RULES.radiusM.code, 'radiusM must be a finite positive number');
      }
      break;
    case 'SCATTER':
      if (!FORMATION_FIELD_RULES.radiusM.holds(formation.radiusM)) {
        bag.add(`${path}.radiusM`, FORMATION_FIELD_RULES.radiusM.code, 'radiusM must be a finite positive number');
      }
      if (formation.minSpacingM !== undefined && !FORMATION_FIELD_RULES.spacingM.holds(formation.minSpacingM)) {
        bag.add(`${path}.minSpacingM`, FORMATION_FIELD_RULES.spacingM.code, 'minSpacingM, when present, must be a finite positive number');
      }
      break;
    case 'CHAIN': {
      // Each node is one member and one attack unit of the cast, so a chain longer than the cast's units can never run.
      const maxUnits = castReachLimits().maxAttackUnits;
      if (isPositiveInt(formation.count) && formation.count >= 2 && !chainCountIsValid(formation.count, maxUnits)) {
        bag.add(`${path}.count`, 'CHAIN_JUMPS_INVALID', `a chain has at most ${maxUnits} nodes, one per attack unit of the cast`);
      }
      if (typeof formation.jumpRangeM !== 'number' || !chainRangeIsValid(formation.jumpRangeM)) {
        bag.add(`${path}.jumpRangeM`, 'CHAIN_RANGE_INVALID', `jumpRangeM must lie in [${CHAIN_RANGE_MIN_M}, ${CHAIN_RANGE_MAX_M}] metres`);
      }
      if (typeof formation.pick !== 'string' || !(CHAIN_PICKS_V1 as readonly string[]).includes(formation.pick)) {
        bag.add(`${path}.pick`, 'CHAIN_PICK_INVALID', `pick must be one of ${CHAIN_PICKS_V1.join(', ')}`);
      }
      if (formation.only !== undefined && !(isRecord(formation.only) && typeof formation.only.skillStatus === 'string' && Object.keys(formation.only).length === 1)) {
        bag.add(`${path}.only`, 'CHAIN_FILTER_UNKNOWN', 'only names one skill status: { skillStatus }');
      }
      for (const key of Object.keys(formation)) {
        if (!['kind', 'count', 'jumpRangeM', 'pick', 'only'].includes(key)) bag.add(`${path}.${key}`, 'COMPOSITE_FORMATION_INVALID', `unknown chain field ${key}`);
      }
      break;
    }
    default:
      break;
  }
}

function validateMemberSchedule(schedule: unknown, path: string, bag: IssueBag): void {
  if (!isRecord(schedule)) {
    bag.add(path, 'COMPOSITE_MEMBER_SCHEDULE_INVALID', 'memberSchedule must be an object');
    return;
  }
  const kind = schedule.kind;
  if (typeof kind !== 'string' || !(MEMBER_SCHEDULE_KINDS_V1 as readonly string[]).includes(kind)) {
    bag.add(`${path}.kind`, 'COMPOSITE_MEMBER_SCHEDULE_KIND_UNKNOWN', `unknown member schedule kind ${String(kind)}`);
    return;
  }
  if (kind === 'SEQUENTIAL' && !isPositiveInt(schedule.intervalTicks)) {
    bag.add(`${path}.intervalTicks`, 'COMPOSITE_MEMBER_INTERVAL_INVALID', 'SEQUENTIAL requires an integer intervalTicks of at least 1');
  }
  if (kind === 'STAGGERED') {
    if (!isPositiveInt(schedule.windowTicks)) {
      bag.add(`${path}.windowTicks`, 'COMPOSITE_MEMBER_WINDOW_INVALID', 'STAGGERED requires an integer windowTicks of at least 1');
    }
    // The only sanctioned randomness source is the cast-local seed. Naming it
    // in the data keeps a future `Math.random` variant unrepresentable.
    if (schedule.seedPolicy !== 'CAST_SEED') {
      bag.add(`${path}.seedPolicy`, 'COMPOSITE_MEMBER_SEED_POLICY_UNKNOWN', 'STAGGERED requires seedPolicy CAST_SEED');
    }
  }
}

function validateVolleys(volleys: unknown, path: string, bag: IssueBag): void {
  if (!isRecord(volleys)) {
    bag.add(path, 'COMPOSITE_VOLLEY_INVALID', 'volleys must be an object');
    return;
  }
  const kind = volleys.kind;
  if (typeof kind !== 'string' || !(VOLLEY_SCHEDULE_KINDS_V1 as readonly string[]).includes(kind)) {
    bag.add(`${path}.kind`, 'COMPOSITE_VOLLEY_KIND_UNKNOWN', `unknown volley kind ${String(kind)}`);
    return;
  }
  if (kind === 'ONCE') {
    if (volleys.count !== 1) {
      bag.add(`${path}.count`, 'COMPOSITE_VOLLEY_COUNT_INVALID', 'ONCE must declare count 1');
    }
    return;
  }
  if (!isPositiveInt(volleys.count) || volleys.count < 2) {
    bag.add(`${path}.count`, 'COMPOSITE_VOLLEY_COUNT_INVALID', 'REPEAT requires an integer count of at least 2');
  }
  // Zero-interval repeats would be simultaneity wearing a repeat's clothes:
  // three "separate" thrusts landing on one tick. Whether that is ever legal
  // is a runtime/pricing question (plan P6), so the schema refuses it here.
  if (!isPositiveInt(volleys.intervalTicks)) {
    bag.add(`${path}.intervalTicks`, 'COMPOSITE_VOLLEY_INTERVAL_INVALID', 'REPEAT requires an integer intervalTicks of at least 1');
  }
}

function validatePattern(pattern: unknown, path: string, bag: IssueBag): void {
  if (!isRecord(pattern)) {
    bag.add(path, 'COMPOSITE_PATTERN_INVALID', 'pattern must be an object');
    return;
  }
  if (pattern.schemaVersion !== SPATIAL_ATTACK_PATTERN_V1_SCHEMA_VERSION) {
    bag.add(`${path}.schemaVersion`, 'COMPOSITE_PATTERN_SCHEMA_VERSION_INVALID', `pattern schemaVersion must be ${SPATIAL_ATTACK_PATTERN_V1_SCHEMA_VERSION}`);
  }
  if (pattern.hitGrouping !== 'SHARED_UNION' && pattern.hitGrouping !== 'INDEPENDENT') {
    bag.add(`${path}.hitGrouping`, 'COMPOSITE_HIT_GROUPING_UNKNOWN', `unknown hitGrouping ${String(pattern.hitGrouping)}`);
  }

  validateFormation(pattern.formation, `${path}.formation`, bag);
  validateMemberSchedule(pattern.memberSchedule, `${path}.memberSchedule`, bag);
  validateVolleys(pattern.volleys, `${path}.volleys`, bag);

  // A chain's members each strike their own node, one after another (SEQUENTIAL) or all at once once the path is planned
  // (SIMULTANEOUS): a release window scattered by the seed has no node to wait for, and a union is one attack, not a path.
  if (isRecord(pattern.formation) && pattern.formation.kind === 'CHAIN') {
    if (isRecord(pattern.memberSchedule) && pattern.memberSchedule.kind === 'STAGGERED') {
      bag.add(`${path}.memberSchedule`, 'CHAIN_SCHEDULE_UNSUPPORTED', 'a chain is SEQUENTIAL (a ricochet) or SIMULTANEOUS (a planned chain), never STAGGERED');
    }
    if (pattern.hitGrouping !== 'INDEPENDENT') {
      bag.add(`${path}.hitGrouping`, 'CHAIN_GROUPING_UNSUPPORTED', 'each member of a chain is its own attack at its own node, so a chain is INDEPENDENT');
    }
  }

  // A shared union is one attack unit resolving at one moment. Spreading its
  // members over time would make "when does this single attack happen?"
  // unanswerable, so the first version admits only SIMULTANEOUS. Design spec
  // §4.1; relaxing this needs a defined semantics, not a looser check.
  if (
    pattern.hitGrouping === 'SHARED_UNION' &&
    isRecord(pattern.memberSchedule) &&
    typeof pattern.memberSchedule.kind === 'string' &&
    pattern.memberSchedule.kind !== 'SIMULTANEOUS'
  ) {
    bag.add(
      `${path}.memberSchedule`,
      'COMPOSITE_SHARED_UNION_REQUIRES_SIMULTANEOUS',
      'SHARED_UNION members must be SIMULTANEOUS: a single attack unit resolves at a single moment',
    );
  }
}

function validateTiming(
  timing: unknown,
  path: string,
  stageId: string,
  earlierStageIds: ReadonlySet<string>,
  allStageIds: ReadonlySet<string>,
  eventStageIds: ReadonlySet<string>,
  bag: IssueBag,
): void {
  if (!isRecord(timing)) {
    bag.add(path, 'COMPOSITE_STAGE_TIMING_INVALID', 'timing must be an object');
    return;
  }
  const kind = timing.kind;
  if (typeof kind !== 'string' || !(STAGE_TIMING_KINDS_V1 as readonly string[]).includes(kind)) {
    bag.add(`${path}.kind`, 'COMPOSITE_STAGE_TIMING_KIND_UNKNOWN', `unknown stage timing kind ${String(kind)}`);
    return;
  }
  if (kind === 'AFTER_RELEASE') {
    if (!isNonNegativeInt(timing.offsetTicks)) {
      bag.add(`${path}.offsetTicks`, 'COMPOSITE_STAGE_TIMING_OFFSET_INVALID', 'offsetTicks must be a non-negative integer');
    }
    return;
  }
  if (kind === 'ON_STATUS') {
    validateStatusTiming(timing, path, bag);
    return;
  }
  if (kind !== 'AT_STAGE_FRACTION' && kind !== 'ON_STAGE_EVENT') return;

  const reference = timing.referenceStageId;
  if (typeof reference !== 'string' || reference.trim() === '') {
    bag.add(`${path}.referenceStageId`, 'COMPOSITE_STAGE_TIMING_REFERENCE_INVALID', 'referenceStageId must be a non-empty string');
  } else if (reference === stageId) {
    bag.add(`${path}.referenceStageId`, 'COMPOSITE_STAGE_TIMING_SELF_REFERENCE', 'a stage cannot anchor to its own progress');
  } else if (!allStageIds.has(reference)) {
    bag.add(`${path}.referenceStageId`, 'COMPOSITE_STAGE_TIMING_UNKNOWN_REFERENCE', `referenceStageId ${reference} names no stage in this skill`);
  } else if (!earlierStageIds.has(reference)) {
    // Only backward references are representable, which is what makes a
    // reference cycle impossible by construction rather than merely detected.
    bag.add(`${path}.referenceStageId`, 'COMPOSITE_STAGE_TIMING_FORWARD_REFERENCE', `referenceStageId ${reference} must name an earlier stage`);
  } else if (kind === 'AT_STAGE_FRACTION' && eventStageIds.has(reference)) {
    bag.add(`${path}.referenceStageId`, 'COMPOSITE_STAGE_FRACTION_ON_EVENT_STAGE',
      `stage ${reference} starts when its event happens, so it has no start to take a fraction of`);
  }

  if (kind === 'ON_STAGE_EVENT') {
    validateStageEventTiming(timing, path, bag);
    return;
  }

  // (0, 1]: a stage may fire at the very end of its reference's clock, but
  // "at 0%" is AT_RELEASE and should be authored as such.
  const fraction = timing.fraction;
  if (!isFiniteNumber(fraction) || fraction <= 0 || fraction > 1) {
    bag.add(`${path}.fraction`, 'COMPOSITE_STAGE_TIMING_FRACTION_INVALID', 'fraction must lie in (0, 1]');
  }
}

const STAGE_TIMING_EVENT_KEYS: readonly string[] = Object.freeze(['kind', 'referenceStageId', 'event', 'n', 'per', 'anchor', 'source', 'markId', 'delayTicks', 'statusName', 'consume', 'strength']);
const STATUS_TIMING_KEYS: readonly string[] = Object.freeze(['kind', 'statusName', 'event', 'delayTicks']);

/** A stage released by what happens to one of the cast's skill statuses at the enemy that carries it. */
function validateStatusTiming(timing: Record<string, unknown>, path: string, bag: IssueBag): void {
  for (const key of Object.keys(timing)) {
    if (!STATUS_TIMING_KEYS.includes(key)) bag.add(`${path}.${key}`, 'COMPOSITE_STAGE_TIMING_UNKNOWN_FIELD', `unknown status timing field ${key}`);
  }
  if (typeof timing.statusName !== 'string' || timing.statusName.trim() === '') {
    bag.add(`${path}.statusName`, 'SKILL_STATUS_NAME_INVALID', 'a status hook names the skill status it answers: statusName must be a non-empty string');
  }
  if (typeof timing.event !== 'string' || !(STATUS_HOOK_EVENTS_V1 as readonly string[]).includes(timing.event)) {
    bag.add(`${path}.event`, 'COMPOSITE_STATUS_HOOK_EVENT_INVALID', `a status hook answers one of ${STATUS_HOOK_EVENTS_V1.join(', ')}`);
  }
  if (timing.delayTicks !== undefined && !isNonNegativeInt(timing.delayTicks)) {
    bag.add(`${path}.delayTicks`, 'COMPOSITE_STAGE_EVENT_DELAY_INVALID', 'delayTicks must be a non-negative integer');
  }
}

function validateStageEventTiming(timing: Record<string, unknown>, path: string, bag: IssueBag): void {
  for (const key of Object.keys(timing)) {
    if (!STAGE_TIMING_EVENT_KEYS.includes(key)) {
      bag.add(`${path}.${key}`, 'COMPOSITE_STAGE_TIMING_UNKNOWN_FIELD', `unknown stage event timing field ${key}`);
    }
  }
  const event = timing.event;
  if (typeof event !== 'string' || !(STAGE_EVENT_KINDS_V1 as readonly string[]).includes(event)) {
    bag.add(`${path}.event`, 'COMPOSITE_STAGE_EVENT_UNKNOWN', `unknown stage event ${String(event)}`);
  }
  const per = timing.per;
  if (typeof per !== 'string' || !(STAGE_EVENT_PER_V1 as readonly string[]).includes(per)) {
    bag.add(`${path}.per`, 'COMPOSITE_STAGE_EVENT_PER_INVALID', `per must be one of ${STAGE_EVENT_PER_V1.join(', ')}`);
  } else if (event === 'END' && per !== 'UNIT' && per !== 'CAST') {
    // An attack unit ends once, not once per contact or per target.
    bag.add(`${path}.per`, 'COMPOSITE_STAGE_EVENT_PER_INVALID', 'END happens to a unit: per must be UNIT or CAST');
  } else if (event === 'EVERY_N' && per === 'HIT') {
    bag.add(`${path}.per`, 'COMPOSITE_STAGE_EVENT_PER_INVALID', 'EVERY_N counts contacts in a scope: per must be UNIT, TARGET or CAST');
  }
  if (timing.source !== undefined && (typeof timing.source !== 'string' || !(STAGE_EVENT_SOURCES_V1 as readonly string[]).includes(timing.source))) {
    bag.add(`${path}.source`, 'COMPOSITE_STAGE_EVENT_SOURCE_INVALID', `source must be one of ${STAGE_EVENT_SOURCES_V1.join(', ')}`);
  }
  const anchor = timing.anchor;
  if (typeof anchor !== 'string' || !(STAGE_EVENT_ANCHORS_V1 as readonly string[]).includes(anchor)) {
    bag.add(`${path}.anchor`, 'COMPOSITE_STAGE_EVENT_ANCHOR_INVALID', `anchor must be one of ${STAGE_EVENT_ANCHORS_V1.join(', ')}`);
  }
  if (anchor === 'MARKED') {
    // A detonation of marks answers the one moment the marking stage is over, for every enemy it marked.
    if (event !== 'END' || per !== 'CAST') {
      bag.add(path, 'COMPOSITE_MARKED_TIMING_UNSUPPORTED', 'a detonation of marks is released at the END of its source, per CAST: the marks are all placed by then');
    }
    if (timing.source === 'PRIMARY') {
      bag.add(`${path}.source`, 'COMPOSITE_MARKED_SOURCE_UNSUPPORTED', 'a detonation of marks answers any attack of its source: source must be absent or ANY');
    }
    if (typeof timing.markId !== 'string' || timing.markId.trim() === '') {
      bag.add(`${path}.markId`, 'COMPOSITE_MARK_ID_INVALID', 'a MARKED anchor names the mark it detonates: markId must be a non-empty string');
    }
  } else if (timing.markId !== undefined) {
    bag.add(`${path}.markId`, 'COMPOSITE_STAGE_EVENT_MARK_ID_INVALID', 'markId is only legal on a MARKED anchor');
  }
  if (anchor === 'CARRIERS') {
    if (typeof timing.statusName !== 'string' || timing.statusName.trim() === '') {
      bag.add(`${path}.statusName`, 'SKILL_STATUS_NAME_INVALID', 'a CARRIERS anchor names the skill status whose carriers it sweeps: statusName must be a non-empty string');
    }
    if (timing.consume !== undefined && typeof timing.consume !== 'boolean') bag.add(`${path}.consume`, 'COMPOSITE_STAGE_EVENT_SWEEP_INVALID', 'consume must be a boolean');
    if (timing.strength !== undefined && !(SWEEP_STRENGTHS_V1 as readonly string[]).includes(String(timing.strength))) {
      bag.add(`${path}.strength`, 'COMPOSITE_STAGE_EVENT_SWEEP_INVALID', `strength must be one of ${SWEEP_STRENGTHS_V1.join(', ')}`);
    }
  } else {
    for (const field of ['statusName', 'consume', 'strength'] as const) {
      if (timing[field] !== undefined) bag.add(`${path}.${field}`, 'COMPOSITE_STAGE_EVENT_SWEEP_INVALID', `${field} is only legal on a CARRIERS anchor`);
    }
  }
  if (timing.delayTicks !== undefined && !isNonNegativeInt(timing.delayTicks)) {
    bag.add(`${path}.delayTicks`, 'COMPOSITE_STAGE_EVENT_DELAY_INVALID', 'delayTicks must be a non-negative integer');
  }
  if (event === 'EVERY_N') {
    // Every 1st hit is just HIT; refusing it keeps one spelling per meaning.
    if (typeof timing.n !== 'number' || !Number.isInteger(timing.n) || timing.n < 2) {
      bag.add(`${path}.n`, 'COMPOSITE_STAGE_EVENT_N_INVALID', 'EVERY_N requires an integer n of at least 2');
    }
  } else if (timing.n !== undefined) {
    bag.add(`${path}.n`, 'COMPOSITE_STAGE_EVENT_N_INVALID', 'n is only legal on EVERY_N');
  }
}

const STAGE_KEYS: readonly string[] = Object.freeze([
  'stageId', 'timing', 'application', 'pattern', 'effects', 'motion',
]);

const MOTION_KEYS: Readonly<Record<string, readonly string[]>> = Object.freeze({ ORBIT: ['kind', 'angularSpeedRadPerS'], DASH: ['kind', 'rangeM'] });

/**
 * A travelling stage, and the one application shape an orbit may take.
 *
 * The canonical form is enforced rather than inferred, so the same V2
 * application cannot mean two different things depending on whether a V3
 * field is present. Each axis below has exactly one owner, and an orbit
 * that disagrees with any of them is refused rather than repaired.
 */
function validateStageMotion(stage: Record<string, unknown>, path: string, bag: IssueBag): void {
  const motion = stage.motion;
  if (motion === undefined) return;
  if (!isRecord(motion)) {
    bag.add(`${path}.motion`, 'COMPOSITE_STAGE_MOTION_INVALID', 'stage motion must be an object');
    return;
  }
  const keys = typeof motion.kind === 'string' ? MOTION_KEYS[motion.kind] : undefined;
  if (keys === undefined) {
    bag.add(`${path}.motion.kind`, 'COMPOSITE_STAGE_MOTION_KIND_UNKNOWN', `unknown stage motion kind ${String(motion.kind)}`);
    return;
  }
  for (const key of Object.keys(motion)) {
    if (!keys.includes(key)) {
      bag.add(`${path}.motion.${key}`, 'COMPOSITE_STAGE_MOTION_UNKNOWN_FIELD', `unknown stage motion field ${key}`);
    }
  }
  if (motion.kind === 'DASH') {
    validateDashMotion(stage, motion, path, bag);
    return;
  }

  const speed = motion.angularSpeedRadPerS;
  if (typeof speed !== 'number' || !Number.isFinite(speed) || speed === 0) {
    // Zero is not a slow orbit, it is a stationary ring, and that shape is
    // not registered. Refused rather than admitted as a degenerate orbit.
    bag.add(`${path}.motion.angularSpeedRadPerS`, 'COMPOSITE_ORBIT_ANGULAR_SPEED_INVALID',
      'orbit angular speed must be a finite non-zero number of radians per second');
  }

  const pattern = stage.pattern;
  if (isRecord(pattern)) {
    // RING and only RING. `SINGLE` carries no radius, so a single-orb
    // orbit has nowhere to say how wide it is — and RING itself requires a
    // count of at least two (`spatial-pattern.ts`), a shared formation rule
    // this stage does not get to relax. A one-orb orbit is therefore not
    // expressible yet; that is recorded rather than worked around.
    const formation = isRecord(pattern.formation) ? String(pattern.formation.kind) : '';
    if (formation !== 'RING') {
      bag.add(`${path}.pattern.formation`, 'COMPOSITE_ORBIT_FORMATION_UNSUPPORTED',
        'an orbit is a RING: its radius, count and phases are the formation\'s, not the motion\'s');
    }
    if (pattern.hitGrouping !== 'INDEPENDENT') {
      bag.add(`${path}.pattern.hitGrouping`, 'COMPOSITE_ORBIT_HIT_GROUPING_UNSUPPORTED',
        'each orb keeps its own hit map, so an orbit stage is INDEPENDENT');
    }
  }

  const application = stage.application;
  if (!isRecord(application)) return;
  const motionKind = isRecord(application.motion) ? String(application.motion.kind) : '';
  if (motionKind !== 'INSTANT') {
    bag.add(`${path}.application.motion`, 'COMPOSITE_ORBIT_APPLICATION_MOTION_UNSUPPORTED',
      'the orbit owns the path, so the application itself does not travel: application.motion must be INSTANT');
  }
  const placement = isRecord(application.placement) ? String(application.placement.kind) : '';
  if (placement !== 'CASTER' && placement !== 'RESOLVED_POINT') {
    bag.add(`${path}.application.placement`, 'COMPOSITE_ORBIT_PLACEMENT_UNSUPPORTED',
      'the orbit centre is the placement: CASTER follows the caster, RESOLVED_POINT stays put');
  }
  const geometry = application.geometry;
  const skillOwnedCircle = isRecord(geometry) && geometry.owner === 'SKILL_OWNED'
    && isRecord(geometry.shape) && geometry.shape.kind === 'CIRCLE';
  if (!skillOwnedCircle) {
    bag.add(`${path}.application.geometry`, 'COMPOSITE_ORBIT_GEOMETRY_UNSUPPORTED',
      'the orb\'s contact radius is the application geometry: a skill-owned CIRCLE');
  }
  const lifecycle = application.lifecycle;
  if (!isRecord(lifecycle) || lifecycle.kind !== 'PERSISTENT') {
    bag.add(`${path}.application.lifecycle`, 'COMPOSITE_ORBIT_LIFECYCLE_UNSUPPORTED',
      'an orbit lives for a time, so its lifetime is the application lifecycle: PERSISTENT');
  }
  const contactPolicy = application.contactPolicy;
  const hitPolicy = isRecord(contactPolicy) ? String(contactPolicy.hitPolicy) : '';
  if (hitPolicy !== 'COOLDOWN_PER_TARGET' && hitPolicy !== 'ONCE_PER_TARGET') {
    bag.add(`${path}.application.contactPolicy`, 'COMPOSITE_ORBIT_CONTACT_POLICY_UNSUPPORTED',
      'an orb either hits a target once or on a rehit cooldown; EVERY_TICK is not an orbit policy');
  }
}

/**
 * A dash stage, and the one application shape it strikes with: a skill-owned instant circle at the caster, struck where
 * the caster lands. Its radius must reach an actor goal at an unshortened landing, and a chain it carries can only jump
 * as far as the dash reaches.
 */
function validateDashMotion(stage: Record<string, unknown>, motion: Record<string, unknown>, path: string, bag: IssueBag): void {
  if (typeof motion.rangeM !== 'number' || !dashRangeIsValid(motion.rangeM)) {
    bag.add(`${path}.motion.rangeM`, 'DASH_RANGE_INVALID', `a dash moves the caster a finite distance in (0, ${DASH_ATTACK_RANGE_MAX_M}] metres`);
  }
  const application = stage.application;
  if (!isRecord(application)) return;
  const geometry = application.geometry;
  const shape = isRecord(geometry) && geometry.owner === 'SKILL_OWNED' && isRecord(geometry.shape) && geometry.shape.kind === 'CIRCLE' ? geometry.shape : undefined;
  const motionKind = isRecord(application.motion) ? application.motion.kind : undefined;
  const placement = isRecord(application.placement) ? application.placement.kind : undefined;
  const lifecycle = isRecord(application.lifecycle) ? application.lifecycle.kind : undefined;
  if (shape === undefined || motionKind !== 'INSTANT' || placement !== 'CASTER' || lifecycle !== 'INSTANT') {
    bag.add(`${path}.application`, 'DASH_APPLICATION_UNSUPPORTED', 'a dash strikes where the caster lands: its application is a skill-owned instant circle at the CASTER');
    return;
  }
  if (typeof shape.radiusM !== 'number' || !dashRadiusIsValid(shape.radiusM)) {
    bag.add(`${path}.application.geometry.shape.radiusM`, 'DASH_RADIUS_TOO_SMALL', `a dash's strike must reach the goal it lands next to: its radius is at least the caster's ${DASH_RADIUS_MIN_M} m`);
  }
  const formation = isRecord(stage.pattern) && isRecord(stage.pattern.formation) ? stage.pattern.formation : undefined;
  if (formation?.kind === 'CHAIN' && typeof formation.jumpRangeM === 'number' && typeof motion.rangeM === 'number' && !dashChainJumpIsReachable(formation.jumpRangeM, motion.rangeM)) {
    bag.add(`${path}.pattern.formation.jumpRangeM`, 'DASH_CHAIN_RANGE_EXCEEDS_DASH', `a ${formation.jumpRangeM} m jump is farther than the ${motion.rangeM} m dash: a jump is measured from the node, the caster stands a body short of it`);
  }
}

/** The facts the same-tick rule reads of one canonical stage. */
function dashStageFacts(stage: Record<string, unknown>): DashStageFacts | undefined {
  const pattern = stage.pattern;
  if (!isRecord(pattern) || !isRecord(pattern.formation) || !isRecord(pattern.memberSchedule) || !isRecord(pattern.volleys) || !isRecord(stage.timing)) return undefined;
  const schedule = pattern.memberSchedule;
  const volleys = pattern.volleys;
  return {
    stageId: String(stage.stageId), dash: isRecord(stage.motion) && stage.motion.kind === 'DASH',
    members: Number(pattern.formation.count), memberSchedule: schedule.kind as DashStageFacts['memberSchedule'],
    memberIntervalTicks: schedule.kind === 'SEQUENTIAL' ? Number(schedule.intervalTicks) : 0,
    volleys: Number(volleys.count), volleyIntervalTicks: volleys.kind === 'REPEAT' ? Number(volleys.intervalTicks) : 0,
    timing: stage.timing as unknown as StageTimingV1,
  };
}

/** No two dash members of one cast can release on the same tick (`dashScheduleRefusals`). */
function validateDashSchedule(stages: readonly Record<string, unknown>[], bag: IssueBag): void {
  const facts = stages.flatMap((stage) => { const fact = dashStageFacts(stage); return fact === undefined ? [] : [fact]; });
  for (const refusal of dashScheduleRefusals(facts)) {
    const index = stages.findIndex((stage) => stage.stageId === refusal.stageId);
    bag.add(`stages[${index}].pattern`, refusal.code, refusal.message);
  }
}

function validateStage(
  stage: unknown,
  index: number,
  seenIds: Set<string>,
  allStageIds: ReadonlySet<string>,
  eventStageIds: ReadonlySet<string>,
  bag: IssueBag,
): void {
  const path = `stages[${index}]`;
  if (!isRecord(stage)) {
    bag.add(path, 'COMPOSITE_STAGE_INVALID', 'stage must be an object');
    return;
  }

  const stageId = stage.stageId;
  let resolvedId = '';
  if (typeof stageId !== 'string' || stageId.trim() === '') {
    bag.add(`${path}.stageId`, 'COMPOSITE_STAGE_ID_INVALID', 'stageId must be a non-empty, non-blank string');
  } else if (seenIds.has(stageId)) {
    bag.add(`${path}.stageId`, 'COMPOSITE_STAGE_ID_DUPLICATE', `stageId ${stageId} is already used by an earlier stage`);
  } else {
    resolvedId = stageId;
  }

  if (!isRecord(stage.application)) {
    bag.add(`${path}.application`, 'COMPOSITE_STAGE_APPLICATION_MISSING', 'stage requires an application');
  }
  if (!Array.isArray(stage.effects)) {
    bag.add(`${path}.effects`, 'COMPOSITE_STAGE_EFFECTS_MISSING', 'stage requires an effects array');
  }

  validateTiming(stage.timing, `${path}.timing`, resolvedId, new Set(seenIds), allStageIds, eventStageIds, bag);
  validatePattern(stage.pattern, `${path}.pattern`, bag);
  validateStageMotion(stage, path, bag);

  // Excess keys are refused, as `validateCanonicalSkillV2` already refuses
  // them for V2 (`contract.ts` `unknown`). Without this a document carrying
  // a field this build does not know — `motion`, before it existed — passes
  // validation and has that field silently ignored, executing as something
  // the author did not write. Removing this check returns V3 to fail-open.
  for (const key of Object.keys(stage)) {
    if (!STAGE_KEYS.includes(key)) {
      bag.add(`${path}.${key}`, 'COMPOSITE_STAGE_UNKNOWN_FIELD', `unknown stage field ${key}`);
    }
  }

  if (resolvedId !== '') seenIds.add(resolvedId);
}

/**
 * Validate a composite skill's structure.
 *
 * On success the input is returned branded, unchanged and in its authored
 * order — this function never normalizes. The per-stage `application` and
 * `effects` graphs are checked only for presence here; their contents stay
 * owned by `validateCanonicalSkillV2`, which the composite compiler runs
 * per stage (plan P3) rather than this validator duplicating it.
 */
export function validateCompositeSkill(input: unknown): CompositeValidationResult {
  const bag = new IssueBag();

  if (!isRecord(input)) {
    bag.add('', 'COMPOSITE_INPUT_INVALID', 'input must be an object');
    return { ok: false, issues: bag.issues };
  }
  if (input.schemaVersion !== CANONICAL_SKILL_V3_SCHEMA_VERSION) {
    bag.add('schemaVersion', 'COMPOSITE_SCHEMA_VERSION_INVALID', `schemaVersion must be ${CANONICAL_SKILL_V3_SCHEMA_VERSION}`);
    return { ok: false, issues: bag.issues };
  }
  if (typeof input.id !== 'string' || input.id.trim() === '') {
    bag.add('id', 'COMPOSITE_SKILL_ID_INVALID', 'id must be a non-empty string');
  }
  if ('application' in input) {
    bag.add('application', 'COMPOSITE_ROOT_APPLICATION_PRESENT', 'a V3 skill carries applications on its stages, not on its root');
  }
  if ('effects' in input) {
    bag.add('effects', 'COMPOSITE_ROOT_EFFECTS_PRESENT', 'a V3 skill carries effects on its stages, not on its root');
  }

  const stages = input.stages;
  if (!Array.isArray(stages)) {
    bag.add('stages', 'COMPOSITE_STAGES_INVALID', 'stages must be an array');
    return { ok: false, issues: bag.issues };
  }
  if (stages.length === 0) {
    bag.add('stages', 'COMPOSITE_STAGES_EMPTY', 'a skill must declare at least one attack stage');
    return { ok: false, issues: bag.issues };
  }

  // Collected up front so an unknown reference and a forward reference are
  // distinguishable: both are rejected, but they tell the author different things.
  const allStageIds = new Set<string>();
  for (const stage of stages) {
    if (isRecord(stage) && typeof stage.stageId === 'string') allStageIds.add(stage.stageId);
  }

  const eventStageIds = new Set<string>();
  for (const stage of stages) {
    if (isRecord(stage) && typeof stage.stageId === 'string' && isRecord(stage.timing) && (stage.timing.kind === 'ON_STAGE_EVENT' || stage.timing.kind === 'ON_STATUS')) {
      eventStageIds.add(stage.stageId);
    }
  }

  const seenIds = new Set<string>();
  stages.forEach((stage, index) => validateStage(stage, index, seenIds, allStageIds, eventStageIds, bag));
  if (bag.issues.length === 0) validateStatusDefinitions(input.statuses, stages as readonly Record<string, unknown>[], bag);
  if (bag.issues.length === 0) validateSkillStatuses(stages as readonly Record<string, unknown>[], input.statuses, bag);
  if (bag.issues.length === 0) validateElementStatuses(stages as readonly Record<string, unknown>[], bag);
  if (bag.issues.length === 0) validateDotKeys(stages as readonly Record<string, unknown>[], bag);
  if (bag.issues.length === 0) validateMarks(stages as readonly Record<string, unknown>[], bag);
  if (bag.issues.length === 0) validateChainFilters(stages as readonly Record<string, unknown>[], input.statuses, bag);
  if (bag.issues.length === 0) validateDashSchedule(stages as readonly Record<string, unknown>[], bag);

  if (bag.issues.length > 0) return { ok: false, issues: bag.issues };
  return { ok: true, skill: input as unknown as CanonicalSkillV3 };
}

const STATUS_DEFINITION_KEYS: readonly string[] = Object.freeze(['name', 'durationS', 'until', 'stacks', 'tickS']);

/** The shape of each entry of the skill's `statuses`; what the entries mean together is `validateSkillStatuses`. */
function validateStatusDefinitions(statuses: unknown, stages: readonly Record<string, unknown>[], bag: IssueBag): void {
  if (statuses === undefined) return;
  if (!Array.isArray(statuses)) {
    bag.add('statuses', 'COMPOSITE_STATUSES_INVALID', 'statuses must be an array');
    return;
  }
  const stageIds = new Set(stages.map((stage) => stage.stageId));
  statuses.forEach((entry, index) => {
    const path = `statuses[${index}]`;
    if (!isRecord(entry)) {
      bag.add(path, 'COMPOSITE_STATUSES_INVALID', 'a status definition must be an object');
      return;
    }
    for (const key of Object.keys(entry)) {
      if (!STATUS_DEFINITION_KEYS.includes(key)) bag.add(`${path}.${key}`, 'COMPOSITE_STATUS_UNKNOWN_FIELD', `unknown status field ${key}`);
    }
    if (typeof entry.name !== 'string') bag.add(`${path}.name`, 'SKILL_STATUS_NAME_INVALID', 'name must be a string');
    if (entry.durationS !== undefined && !isFinitePositive(entry.durationS)) bag.add(`${path}.durationS`, 'BAD_EFFECT_DURATION', 'durationS must be a finite positive number');
    if (entry.until !== undefined) {
      if (!isRecord(entry.until) || typeof entry.until.stageId !== 'string') bag.add(`${path}.until`, 'COMPOSITE_STATUSES_INVALID', 'until names a stage: { stageId }');
      else if (!stageIds.has(entry.until.stageId)) bag.add(`${path}.until.stageId`, 'COMPOSITE_STAGE_TIMING_UNKNOWN_REFERENCE', `until names stage ${entry.until.stageId}, which is not in this skill`);
    }
    if (entry.stacks !== undefined) {
      if (!isRecord(entry.stacks)) bag.add(`${path}.stacks`, 'COMPOSITE_STATUSES_INVALID', 'stacks must be an object');
      else {
        for (const key of Object.keys(entry.stacks)) if (key !== 'max') bag.add(`${path}.stacks.${key}`, 'COMPOSITE_STATUS_UNKNOWN_FIELD', `unknown stacks field ${key}`);
        if (entry.stacks.max !== undefined && !isPositiveInt(entry.stacks.max)) bag.add(`${path}.stacks.max`, 'BAD_EFFECT_VALUE', 'stacks.max must be an integer of at least 1');
      }
    }
  });
}

/** The `status` leaves of an effect list: those at its top level, and whether any hangs under another node. */
export function statusLeaves(effects: unknown): { readonly top: readonly Record<string, unknown>[]; readonly nested: boolean } {
  const top: Record<string, unknown>[] = [];
  let nested = false;
  const visit = (nodes: unknown, isTop: boolean): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!isRecord(node)) continue;
      if (node.kind === 'status') { if (isTop) top.push(node); else nested = true; }
      else if (node.kind === 'condition' || node.kind === 'trigger') visit(node.children, false);
      else if (node.kind === 'secondary_attack') visit(node.effects, false);
    }
  };
  visit(effects, true);
  return { top, nested };
}

/** Every skill status an effect list binds an effect to (`while`), anywhere under it. */
export function whileNames(effects: unknown): string[] {
  const names: string[] = [];
  const visit = (nodes: unknown): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!isRecord(node)) continue;
      if (typeof node.while === 'string') names.push(node.while);
      if (node.kind === 'condition' || node.kind === 'trigger') visit(node.children);
      else if (node.kind === 'secondary_attack') visit(node.effects);
    }
  };
  visit(effects);
  return names;
}

/** The neutral model of a skill's statuses and the stages around them, for `skill-status-rules`. */
export function canonicalStatusModel(stages: readonly Record<string, unknown>[], statuses: unknown): { readonly defs: StatusDef[]; readonly stages: StatusStage[] } {
  const defs: StatusDef[] = (Array.isArray(statuses) ? statuses : []).filter(isRecord).map((entry) => ({
    name: String(entry.name),
    ...(typeof entry.durationS === 'number' ? { durationS: entry.durationS } : {}),
    ...(isRecord(entry.until) ? { untilStage: String(entry.until.stageId) } : {}),
    stacked: isRecord(entry.stacks),
    ...(isRecord(entry.stacks) && typeof entry.stacks.max === 'number' ? { stackMax: entry.stacks.max } : {}),
    ...(typeof entry.tickS === 'number' ? { tickS: entry.tickS } : {}),
  }));
  return {
    defs,
    stages: stages.map((stage): StatusStage => {
      const timing = stage.timing as Record<string, unknown>;
      const bindings = contactBindings(stage).flatMap((binding) => whileNames(binding.effects));
      const base = {
        key: String(stage.stageId),
        creates: statusLeaves(stage.effects).top.map((leaf) => String(leaf.name)),
        whiles: [...whileNames(stage.effects), ...bindings],
      };
      if (timing.kind === 'ON_STATUS') return { ...base, timing: { kind: 'hook', status: String(timing.statusName), event: timing.event as 'APPLY' | 'TICK' | 'END' } };
      if (timing.kind === 'ON_STAGE_EVENT') {
        return { ...base, timing: { kind: 'event', of: String(timing.referenceStageId), ...(timing.anchor === 'CARRIERS'
          ? { sweep: { status: String(timing.statusName), strength: timing.strength === 'STACKS' ? 'STACKS' as const : 'ONE' as const } } : {}) } };
      }
      return { ...base, timing: { kind: 'plain' } };
    }),
  };
}

/** Every (kind, element, name) an effect list holds at any depth: the damage and dot elements it deals and the element statuses it applies. */
function elementFacts(effects: unknown, found: { readonly elements: Set<string>; readonly statuses: string[] }): void {
  if (!Array.isArray(effects)) return;
  for (const node of effects) {
    if (!isRecord(node)) continue;
    if ((node.kind === 'damage' || node.kind === 'dot') && typeof node.damageType === 'string') found.elements.add(node.damageType);
    if (node.kind === 'element_status') found.statuses.push(String(node.name));
    if (node.kind === 'condition' || node.kind === 'trigger') elementFacts(node.children, found);
    else if (node.kind === 'secondary_attack') elementFacts(node.effects, found);
  }
}

/** An element status is what a hit of its element leaves behind: its stage must deal damage or a dot of that element. */
function validateElementStatuses(stages: readonly Record<string, unknown>[], bag: IssueBag): void {
  stages.forEach((stage, index) => {
    const found = { elements: new Set<string>(), statuses: [] as string[] };
    elementFacts(stage.effects, found);
    for (const name of found.statuses) {
      const mismatch = elementStatusMismatch(name, found.elements, String(stage.stageId));
      if (mismatch !== undefined) bag.add(`stages[${index}].effects`, mismatch.code, mismatch.message);
    }
  });
}

/** How the skill's statuses fit together: who creates them, who binds to and hooks them, and whether their lifetimes can be met. */
function validateSkillStatuses(stages: readonly Record<string, unknown>[], statuses: unknown, bag: IssueBag): void {
  const model = canonicalStatusModel(stages, statuses);
  const indexOf = new Map(stages.map((stage, index) => [String(stage.stageId), index] as const));
  stages.forEach((stage, index) => {
    const { top, nested } = statusLeaves(stage.effects);
    const inBindings = contactBindings(stage).some((binding) => statusLeaves(binding.effects).top.length > 0 || statusLeaves(binding.effects).nested);
    if (nested || inBindings) {
      bag.add(`stages[${index}].effects`, 'SKILL_STATUS_NOT_TOP_LEVEL', 'a status effect is a top-level effect of its stage: it is applied before the stage\'s other effects, so a bound effect finds it');
    }
    if (top.some((leaf) => typeof leaf.name !== 'string' || leaf.name.trim() === '')) {
      bag.add(`stages[${index}].effects`, 'SKILL_STATUS_NAME_INVALID', 'a status effect names the skill status it applies');
    }
  });
  for (const refusal of checkStatusRules(model.defs, model.stages, { minTickS: SVP_0_1.limits.minTickS })) {
    const index = refusal.stage === undefined ? undefined : indexOf.get(refusal.stage);
    bag.add(index === undefined ? 'statuses' : `stages[${index}]`, refusal.code, refusal.message);
  }
}

/** The `mark` leaves of an effect list: those at its top level, and whether any hangs under another node. */
export function markLeaves(effects: unknown): { readonly top: readonly Record<string, unknown>[]; readonly nested: boolean } {
  const top: Record<string, unknown>[] = [];
  let nested = false;
  const visit = (nodes: unknown, isTop: boolean): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!isRecord(node)) continue;
      if (node.kind === 'mark') { if (isTop) top.push(node); else nested = true; }
      else if (node.kind === 'condition' || node.kind === 'trigger') visit(node.children, false);
      else if (node.kind === 'secondary_attack') visit(node.effects, false);
    }
  };
  visit(effects, true);
  return { top, nested };
}

/**
 * The leaf kinds a detonation of marks may carry: the ones the runtime delivers `stacks` times the value of
 * (`runtime.ts` `applyLeaf`, `cast-marks.ts` `stackedCcDurationS`) as the price counts them. A silence or a disarm has
 * no price; the rest (a heal, a shield, a buff, a dash, a cooldown or resource change, a heal reduction, a damage
 * amplification) are priced by the stack weight but have no stack rule yet.
 */
const MARK_DETONATION_LEAF_KINDS: ReadonlySet<string> = new Set(['damage', 'dot', 'knockback', 'pull', 'stun', 'root', 'slow']);

/** The kind of every leaf under an effect list, through conditions, triggers and follow-up attacks (a mark is refused elsewhere). */
function leafKindsUnder(effects: unknown): string[] {
  const kinds: string[] = [];
  const visit = (nodes: unknown): void => {
    if (!Array.isArray(nodes)) return;
    for (const node of nodes) {
      if (!isRecord(node) || typeof node.kind !== 'string') continue;
      if (node.kind === 'condition' || node.kind === 'trigger') visit(node.children);
      else if (node.kind === 'secondary_attack') visit(node.effects);
      else if (node.kind !== 'mark') kinds.push(node.kind);
    }
  };
  visit(effects);
  return kinds;
}

/** The effects a weapon-contact stage binds to its contact regions. */
export function contactBindings(stage: Record<string, unknown>): readonly Record<string, unknown>[] {
  const geometry = isRecord(stage.application) && isRecord(stage.application.geometry) ? stage.application.geometry : undefined;
  return geometry?.owner === 'WEAPON_CONTACT' && Array.isArray(geometry.contactBindings) ? geometry.contactBindings.filter(isRecord) : [];
}

/**
 * Cast-scoped marks (a `mark` leaf on a stage, detonated by one `MARKED` event stage). A mark is a count of
 * contacts, so every rule here keeps "one contact, one stack" true and keeps the one event stage's price and
 * release limit the same as the runtime's.
 */
function validateMarks(stages: readonly Record<string, unknown>[], bag: IssueBag): void {
  const markedBy = new Map<string, number>();
  const detonations: number[] = [];
  const eventStages: number[] = [];
  stages.forEach((stage, index) => {
    const path = `stages[${index}]`;
    const timing = stage.timing as Record<string, unknown>;
    const { top, nested } = markLeaves(stage.effects);
    const inBindings = contactBindings(stage).some((binding) => markLeaves(binding.effects).top.length > 0 || markLeaves(binding.effects).nested);
    if (timing.kind === 'ON_STAGE_EVENT') {
      eventStages.push(index);
      if (timing.anchor === 'MARKED') detonations.push(index);
    }
    if (nested || inBindings) {
      bag.add(`${path}.effects`, 'COMPOSITE_MARK_NOT_TOP_LEVEL', 'a mark is a top-level effect of its stage: under a condition, trigger, follow-up attack or contact binding one contact would no longer be one stack');
    }
    const ids = new Set<string>();
    for (const mark of top) {
      const markId = mark.markId;
      if (typeof markId !== 'string' || markId.trim() === '') {
        bag.add(`${path}.effects`, 'COMPOSITE_MARK_ID_INVALID', 'markId must be a non-empty string');
        continue;
      }
      if (ids.has(markId)) bag.add(`${path}.effects`, 'COMPOSITE_MARK_ID_NOT_UNIQUE', `stage ${String(stage.stageId)} stacks mark ${markId} twice: one contact must leave one stack`);
      ids.add(markId);
      const earlier = markedBy.get(markId);
      if (earlier !== undefined && earlier !== index) bag.add(`${path}.effects`, 'COMPOSITE_MARK_ID_NOT_UNIQUE', `mark ${markId} is already stacked by stage ${String(stages[earlier]!.stageId)}: a mark has one stacking stage`);
      else markedBy.set(markId, index);
    }
    if (top.length > 0) {
      if (timing.kind === 'ON_STAGE_EVENT' || timing.kind === 'ON_STATUS') {
        bag.add(`${path}.timing`, timing.kind === 'ON_STAGE_EVENT' && timing.anchor === 'MARKED' ? 'COMPOSITE_MARK_IN_DETONATION' : 'COMPOSITE_MARK_STAGE_EVENT_RELEASED',
          timing.kind === 'ON_STAGE_EVENT' && timing.anchor === 'MARKED'
            ? `stage ${String(stage.stageId)} detonates marks and so cannot stack one`
            : `stage ${String(stage.stageId)} is released by an event and so cannot stack a mark: how many contacts it makes is a count the price does not give`);
      }
      const replaced = contactBindings(stage).some((binding) => binding.mode === 'REPLACE_DEFAULT');
      if (replaced) {
        bag.add(`${path}.application.geometry.contactBindings`, 'COMPOSITE_MARK_WITH_REPLACE_DEFAULT_BINDING', 'a REPLACE_DEFAULT contact binding takes the stage\'s own effects off the struck enemy, so its contact would leave no stack');
      }
    }
  });

  for (const index of detonations) {
    const timing = stages[index]!.timing as Record<string, unknown>;
    const path = `stages[${index}].timing`;
    const markId = timing.markId;
    const reference = stages.findIndex((stage) => stage.stageId === timing.referenceStageId);
    if (typeof markId === 'string' && markedBy.get(markId) !== reference) {
      bag.add(`${path}.markId`, 'COMPOSITE_MARKED_NO_MARKING_STAGE', `stage ${String(timing.referenceStageId)} does not stack mark ${markId}, so there is nothing for stage ${String(stages[index]!.stageId)} to detonate`);
    }
    const detonation = stages[index]!;
    const unsupported = new Set([detonation.effects, ...contactBindings(detonation).map((binding) => binding.effects)]
      .flatMap((effects) => leafKindsUnder(effects)).filter((kind) => !MARK_DETONATION_LEAF_KINDS.has(kind)));
    for (const kind of [...unsupported].sort()) {
      bag.add(`stages[${index}].effects`, 'COMPOSITE_MARK_DETONATION_LEAF_UNSUPPORTED', `stage ${String(detonation.stageId)} detonates marks and so cannot carry a ${kind}: the release delivers its damage, damage over time, displacement, stun, root and slow once per stack, and a ${kind} has no such rule`);
    }
  }
  if (detonations.length > 0 && eventStages.length > 1) {
    bag.add(`stages[${eventStages[1]}].timing`, 'COMPOSITE_MARK_DETONATION_NOT_ONLY_EVENT_STAGE', 'a detonation of marks is the cast\'s only event stage: event stages share the cast\'s release budget, which the price counts per stage');
  }
  const filteredBy = chainFilterNames(stages);
  for (const [markId, index] of markedBy) {
    const consumed = filteredBy.has(markId) || detonations.some((detonation) => {
      const timing = stages[detonation]!.timing as Record<string, unknown>;
      return timing.markId === markId && timing.referenceStageId === stages[index]!.stageId;
    });
    if (!consumed) bag.add(`stages[${index}].effects`, 'COMPOSITE_MARK_NEVER_CONSUMED', `mark ${markId} is stacked but no stage detonates it`);
  }
}

/** The names the chains of a skill filter by. */
function chainFilterNames(stages: readonly Record<string, unknown>[]): ReadonlySet<string> {
  const names = new Set<string>();
  for (const stage of stages) {
    const formation = (stage.pattern as Record<string, unknown>).formation as Record<string, unknown>;
    if (formation.kind === 'CHAIN' && isRecord(formation.only) && typeof formation.only.skillStatus === 'string') names.add(formation.only.skillStatus);
  }
  return names;
}

/**
 * What a chain's `only` names, and the earliest stage before the chain that creates it: one of the cast's own statuses
 * (a `status` leaf of a declared status) or one of its marks (a top-level `mark` leaf: `kind: 'mark'`, `tag: markId`,
 * which is no skill-status definition). A status wins a name both are.
 */
export function chainFilterSource(
  stages: readonly Record<string, unknown>[],
  statuses: unknown,
  chainIndex: number,
  name: string,
): { readonly kind: 'STATUS' | 'MARK'; readonly creatorIndex: number } | undefined {
  const defined = (Array.isArray(statuses) ? statuses : []).some((entry) => isRecord(entry) && entry.name === name);
  const earlier = stages.slice(0, chainIndex);
  const status = defined ? earlier.findIndex((stage) => statusLeaves(stage.effects).top.some((leaf) => leaf.name === name)) : -1;
  if (status >= 0) return { kind: 'STATUS', creatorIndex: status };
  const mark = earlier.findIndex((stage) => markLeaves(stage.effects).top.some((leaf) => leaf.markId === name));
  return mark >= 0 ? { kind: 'MARK', creatorIndex: mark } : undefined;
}

/** The tick a stage's own release is fixed at by the program alone (`AT_RELEASE`, `AFTER_RELEASE`); undefined for any other timing. */
function fixedStartTick(stage: Record<string, unknown>): number | undefined {
  const timing = stage.timing as Record<string, unknown>;
  if (timing.kind === 'AT_RELEASE') return 0;
  return timing.kind === 'AFTER_RELEASE' && typeof timing.offsetTicks === 'number' ? timing.offsetTicks : undefined;
}

/**
 * The last tick a stage releases an attack at, an upper bound for a staggered schedule; undefined when it is not fixed
 * by the program alone (an event or fraction timing, or a chain, whose members come out as it strikes).
 */
function lastReleaseTick(stage: Record<string, unknown>): number | undefined {
  const start = fixedStartTick(stage);
  const pattern = stage.pattern as SpatialAttackPatternV1;
  if (start === undefined || pattern.formation.kind === 'CHAIN') return undefined;
  const expanded = expandSpatialPattern(pattern, 0);
  if (!expanded.ok) return undefined;
  const staggered = pattern.memberSchedule.kind === 'STAGGERED' ? pattern.memberSchedule.windowTicks : 0;
  return start + Math.max(...expanded.units.map((unit) => unit.releaseOffsetTicks)) + staggered;
}

/** The tick by which every unit of a stage has ended: its last release plus its delivery's longest life; undefined when the program alone does not bound either. */
function lastEndTick(stage: Record<string, unknown>): number | undefined {
  const released = lastReleaseTick(stage);
  const life = deliveryMaxLifeTicks(applicationLife(stage.application as SkillApplicationV2));
  return released === undefined || life === undefined ? undefined : released + life;
}

/** What bounds how long an application's attacks live: a flight, a lifetime, a sweep; undefined for a channel or an unlowered shape. */
function applicationLife(application: SkillApplicationV2): Parameters<typeof deliveryMaxLifeTicks>[0] {
  const { motion, lifecycle } = application;
  if (lifecycle.kind === 'SUSTAINED') return undefined;
  if (lifecycle.kind === 'PERSISTENT') return { kind: 'LASTING', durationS: lifecycle.durationS };
  if (motion.kind === 'PROJECTILE') return { kind: 'PROJECTILE', rangeM: motion.maxTravelM, speedMps: motion.speedMps };
  if (motion.kind === 'WEAPON_SWEEP') return { kind: 'LASTING', durationS: motion.durationS };
  return { kind: 'INSTANT' };
}

/** Whether `stage` is released by an event of the stage `creatorId` or of a stage that is itself released by an event of it. */
function isEventOfCreator(stages: readonly Record<string, unknown>[], stage: Record<string, unknown>, creatorId: unknown): boolean {
  let timing = stage.timing as Record<string, unknown>;
  for (let hops = 0; hops <= stages.length && timing.kind === 'ON_STAGE_EVENT'; hops += 1) {
    if (timing.referenceStageId === creatorId) return true;
    timing = stages.find((candidate) => candidate.stageId === timing.referenceStageId)?.timing as Record<string, unknown>;
    if (!isRecord(timing)) return false;
  }
  return false;
}

/** Whether the stage a status ends with settles after the chain's last member releases: the chain's own stage, or a fixed-tick stage that ends later than that release. */
function untilSettlesAfterChain(stages: readonly Record<string, unknown>[], chain: Record<string, unknown>, untilStageId: unknown, lastReleaseOfChain: number | undefined): boolean {
  if (untilStageId === chain.stageId) return true;
  const ending = stages.find((candidate) => candidate.stageId === untilStageId);
  const endsAt = ending === undefined ? undefined : lastEndTick(ending);
  return lastReleaseOfChain !== undefined && endsAt !== undefined && endsAt > lastReleaseOfChain;
}

/**
 * What a chain's `only` names: one of the cast's own statuses or marks, created by a stage authored before the chain,
 * and created before the chain can pick (`chainFilterTimingIsLegal`). An element status is engine data whose carriers
 * are other casts' too: a chain across them is the element's own discharge, not a skill's formation.
 */
function validateChainFilters(stages: readonly Record<string, unknown>[], statuses: unknown, bag: IssueBag): void {
  stages.forEach((stage, index) => {
    const pattern = stage.pattern as Record<string, unknown>;
    const formation = pattern.formation as Record<string, unknown>;
    const name = formation.kind === 'CHAIN' && isRecord(formation.only) ? formation.only.skillStatus : undefined;
    if (typeof name !== 'string') return;
    const path = `stages[${index}].pattern.formation.only.skillStatus`;
    if (ELEMENT_STATUS_NAMES.includes(name)) { bag.add(path, 'CHAIN_FILTER_ELEMENT', `${name} is an element status: a chain picks only among the carriers of the cast's own statuses`); return; }
    const source = chainFilterSource(stages, statuses, index, name);
    if (source === undefined) { bag.add(path, 'CHAIN_FILTER_UNKNOWN', `only names ${name}, which no earlier stage of this skill creates as a status or a mark`); return; }
    const creator = stages[source.creatorIndex]!;
    const creates = (candidate: Record<string, unknown>): boolean => source.kind === 'STATUS'
      ? statusLeaves(candidate.effects).top.some((leaf) => leaf.name === name)
      : markLeaves(candidate.effects).top.some((leaf) => leaf.markId === name);
    if (stages.filter(creates).length > 1) {
      bag.add(path, 'CHAIN_FILTER_MULTIPLE_CREATORS', `more than one stage creates ${name}: a chain picks among the carriers of one stage`);
      return;
    }
    const creatorFormation = (creator.pattern as Record<string, unknown>).formation as Record<string, unknown>;
    if (creatorFormation.kind === 'CHAIN') {
      bag.add(path, 'CHAIN_FILTER_CHAIN_CREATOR', `${name} is created by the chain stage ${String(creator.stageId)}: its carriers are spread over the chain's nodes, which no carrier-field model prices yet`);
      return;
    }
    const chainTiming = stage.timing as Record<string, unknown>;
    if (chainTiming.kind === 'ON_STAGE_EVENT' && chainTiming.anchor === 'EVENT_POINT' && !(chainTiming.event === 'HIT' && chainTiming.referenceStageId === creator.stageId)) {
      bag.add(`stages[${index}].timing`, 'CHAIN_FILTER_ANCHOR', `stage ${String(stage.stageId)} starts at an event point other than a hit of ${String(creator.stageId)}: where that point lies relative to the carriers is not modelled yet; start it at the caster, the cast's aim, or a hit of the creator`);
      return;
    }
    const eventOfCreator = isEventOfCreator(stages, stage, creator.stageId);
    if (!chainFilterTimingIsLegal({ eventOfCreator, firstReleaseTick: fixedStartTick(stage) }, { lastEndTick: lastEndTick(creator) })) {
      bag.add(`stages[${index}].timing`, 'CHAIN_FILTER_TIMING', `stage ${String(stage.stageId)} picks among the carriers of ${name} before stage ${String(creator.stageId)} has created them: release it by an event of that stage or strictly after its last attack has ended`);
      return;
    }
    const schedule = pattern.memberSchedule as SpatialAttackPatternV1['memberSchedule'];
    const span = chainSpanTicks({
      count: formation.count as number,
      ricochetIntervalTicks: schedule.kind === 'SEQUENTIAL' ? schedule.intervalTicks : undefined,
      memberLifeTicks: deliveryMaxLifeTicks(applicationLife(stage.application as SkillApplicationV2)),
    });
    const firstRelease = fixedStartTick(stage);
    const creatorEnd = lastEndTick(creator);
    const lastRelease = eventOfCreator || firstRelease === undefined || span === undefined ? undefined : firstRelease + span;
    // A carrier's status runs from its own application, and the earliest is the creator's first release; the chain's
    // last pick is at most its own last release (an event of the creator itself fires by the creator's last end).
    const timing = stage.timing as Record<string, unknown>;
    const directEvent = eventOfCreator && timing.referenceStageId === creator.stageId;
    const delay = typeof timing.delayTicks === 'number' ? timing.delayTicks : 0;
    // A chain released through another event stage has no last pick the program bounds, so a status that can end on
    // its own is refused for it (conservative: it may refuse a program whose events happen to fire early enough).
    const chainLast = !eventOfCreator ? lastRelease : directEvent && creatorEnd !== undefined && span !== undefined ? creatorEnd + delay + span : undefined;
    const creatorFirst = fixedStartTick(creator);
    const gap = chainLast === undefined || creatorFirst === undefined ? undefined : chainLast - creatorFirst;
    const definition = (Array.isArray(statuses) ? statuses : []).find((entry): entry is Record<string, unknown> => isRecord(entry) && entry.name === name);
    const life: ChainCarrierLife = source.kind === 'MARK' || definition === undefined ? { kind: 'CAST' }
      : typeof definition.durationS === 'number' ? { kind: 'DURATION', ticks: chainStatusDurationTicks(definition.durationS) }
      : isRecord(definition.until) ? { kind: 'UNTIL_STAGE', settlesAfterChain: untilSettlesAfterChain(stages, stage, definition.until.stageId, chainLast) }
      : { kind: 'CAST' };
    if (!chainCarriersSurvive(life, gap)) {
      bag.add(`stages[${index}].timing`, 'CHAIN_FILTER_STATUS_EXPIRES', `status ${name} can end before stage ${String(stage.stageId)} releases its last member: it must outlast the chain, or end when the chain's own stage settles`);
    }
  });
}

/** Type guard for the branded form, for callers that already hold a validated value. */
export function isCompositeSkillInput(value: unknown): value is CanonicalSkillV3Input {
  return isRecord(value) && value.schemaVersion === CANONICAL_SKILL_V3_SCHEMA_VERSION;
}

export type { AttackStageV1, SpatialAttackPatternV1 };

/** Two authored payloads of one refresh key replace rather than add their magnitudes. */
function validateDotKeys(stages: readonly Record<string, unknown>[], bag: IssueBag): void {
  stages.forEach((stage, index) => {
    for (const issue of dotRefreshKeyIssues(stage.effects, stage.application, `stages[${index}].effects`, String(stage.stageId))) {
      bag.add(issue.path, issue.code, issue.message);
    }
  });
}
